import { Injectable, signal } from '@angular/core'
import {
    OnboardingCategory,
    OnboardingItem,
    OnboardingProgress,
} from 'src/app/shared/interfaces/onboarding/onboarding-item.interface'

@Injectable({
    providedIn: 'root',
})
export class OnboardingService {
    private readonly STORAGE_KEY = 'onboarding_progress'

    private onboardingItems = signal<OnboardingItem[]>([
        {
            id: 'welcome',
            title: 'Welcome to Surefire Software',
            description: 'Get started with your account setup',
            completed: false,
            icon: 'hand-wave',
            category: OnboardingCategory.GettingStarted,
            order: 1,
        },
        {
            id: 'company-profile',
            title: 'Complete Company Profile',
            description: 'Add your company details, set your general settings, logos, and contact information',
            completed: false,
            icon: 'building',
            route: '/admin/general-settings',
            category: OnboardingCategory.GettingStarted,
            order: 2,
        },
        {
            id: 'add-staff',
            title: 'Add New Staff Member',
            description: 'Set up your team by adding staff members',
            completed: false,
            icon: 'person-plus',
            route: '/staff/0',
            category: OnboardingCategory.Setup,
            order: 3,
        },
        {
            id: 'add-vehicle',
            title: 'Add Your First Vehicle',
            description: 'Register vehicles for scheduling and tracking',
            completed: false,
            icon: 'truck',
            route: '/admin/database/vehicles',
            category: OnboardingCategory.Setup,
            order: 4,
        },
        {
            id: 'appointment-types',
            title: 'Configure Appointment Types',
            description: 'Set up the types of appointments you offer',
            completed: false,
            icon: 'calendar-check',
            route: '/admin/database/appointment-types',
            category: OnboardingCategory.Setup,
            order: 5,
        },
        {
            id: 'add-customer',
            title: 'Add Your First Customer',
            description: 'Create your first customer record',
            completed: false,
            icon: 'person-circle',
            route: '/customers/0',
            category: OnboardingCategory.Setup,
            order: 6,
        },
        {
            id: 'create-appointment',
            title: 'Schedule Your First Appointment',
            description: 'Book your first appointment in the diary',
            completed: false,
            icon: 'calendar-event',
            route: '/diary',
            category: OnboardingCategory.Setup,
            order: 7,
        },
        {
            id: 'configure-notifications',
            title: 'Set Up Notifications',
            description: 'Configure email and SMS notifications',
            completed: false,
            icon: 'bell',
            route: '/admin/templates/email-templates',
            category: OnboardingCategory.Configuration,
            order: 8,
        },
        {
            id: 'integrate-accounting',
            title: 'Connect Accounting Software',
            description: 'Integrate with QuickBooks or Xero',
            completed: false,
            icon: 'cash-stack',
            route: '/accounts-integration',
            category: OnboardingCategory.Advanced,
            order: 9,
        },
        {
            id: 'mobile-app',
            title: 'Download Mobile App',
            description: 'Get the mobile app for on-the-go access',
            completed: false,
            icon: 'phone',
            category: OnboardingCategory.Advanced,
            order: 10,
        },
    ])

    constructor() {
        this.loadProgress()
    }

    getItems() {
        return this.onboardingItems.asReadonly()
    }

    getProgress(): OnboardingProgress {
        const items = this.onboardingItems()
        const completedItems = items.filter((item) => item.completed).length
        const totalItems = items.length

        return {
            totalItems,
            completedItems,
            percentageComplete: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0,
            lastUpdated: new Date(),
        }
    }

    getItemsByCategory(category: OnboardingCategory): OnboardingItem[] {
        return this.onboardingItems()
            .filter((item) => item.category === category)
            .sort((a, b) => a.order - b.order)
    }

    markAsComplete(itemId: string): void {
        this.onboardingItems.update((items) =>
            items.map((item) => (item.id === itemId ? { ...item, completed: true } : item))
        )
        this.saveProgress()
    }

    markAsIncomplete(itemId: string): void {
        this.onboardingItems.update((items) =>
            items.map((item) => (item.id === itemId ? { ...item, completed: false } : item))
        )
        this.saveProgress()
    }

    toggleCompletion(itemId: string): void {
        this.onboardingItems.update((items) =>
            items.map((item) => (item.id === itemId ? { ...item, completed: !item.completed } : item))
        )
        this.saveProgress()
    }

    resetProgress(): void {
        this.onboardingItems.update((items) => items.map((item) => ({ ...item, completed: false })))
        this.saveProgress()
    }

    dismissOnboarding(): void {
        localStorage.setItem('onboarding_dismissed', 'true')
    }

    showOnboarding(): void {
        localStorage.removeItem('onboarding_dismissed')
    }

    isOnboardingDismissed(): boolean {
        return localStorage.getItem('onboarding_dismissed') === 'true'
    }

    private saveProgress(): void {
        const progress = this.onboardingItems().map((item) => ({
            id: item.id,
            completed: item.completed,
        }))
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(progress))
    }

    private loadProgress(): void {
        const savedProgress = localStorage.getItem(this.STORAGE_KEY)
        if (savedProgress) {
            try {
                const progress = JSON.parse(savedProgress) as { id: string; completed: boolean }[]
                this.onboardingItems.update((items) =>
                    items.map((item) => {
                        const saved = progress.find((p) => p.id === item.id)
                        return saved ? { ...item, completed: saved.completed } : item
                    })
                )
            } catch (error) {
                console.error('Failed to load onboarding progress', error)
            }
        }
    }
}
